import { ArrowLeftRight, TriangleAlert } from 'lucide-react-native';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import { PointButton } from './PointButton';
import { swapParts } from '../../model/swapParts';
import type { Segment } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';
import { SourceLink } from '../shared/SourceLink';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type PointRowProps = {
  segment: Segment;
  onRemove: (segment: Segment) => void;
  onAdd: (segment: Segment) => void;
  onSwap: (segment: Segment) => void;
};

/**
 * One point the draft considered, with a minus to take it out of the reply or,
 * while out, a plus to put it in. Drafted sentences and suggestions are the
 * same row.
 *
 * Removing and adding happen only through that button, never by tapping the
 * sentence, so a tap made while reading changes nothing. A point out of the
 * reply moves to the list under "Not in your reply", and its plus is the undo.
 * That list's heading and the swapped icon carry the state, so the text stays
 * full strength in both: a strikethrough made the left-out points hard to read
 * for exactly the expert deciding whether to add them back.
 * A must-fix suggestion says "Must fix" in words beside its warning icon
 * while it is out of the reply; once added there is nothing left to fix.
 *
 * A swappable detail is underlined where it sits, and its swap button says
 * what the detail becomes; it is hidden on a point out of the reply, which is
 * not there to change.
 */
export const PointRow = memo(function PointRow({
  segment,
  onRemove,
  onAdd,
  onSwap,
}: PointRowProps) {
  const isOut = segment.struck;

  const swap = isOut ? null : swapParts(segment);

  const textClass = 'text-base text-typography-900';

  return (
    <VStack testID={`reply-draft-segment-${segment.id}`} className="rounded-md">
      <HStack className="items-start gap-1">
        <VStack className="flex-1 gap-1 px-2 py-3">
          {segment.severity === 'must_fix' && isOut ? (
            <HStack
              className="items-center gap-1"
              testID={`reply-draft-must-fix-tag-${segment.id}`}>
              <Icon as={TriangleAlert} size="xs" className="text-error-700" />
              <Text size="xs" className="font-bold uppercase text-error-700">
                {REPLY_DRAFT_COPY.mustFix}
              </Text>
            </HStack>
          ) : null}

          <Text className={textClass}>
            {swap ? (
              <>
                {swap.before}
                <Text className={twJoin(textClass, 'font-semibold underline')}>{swap.detail}</Text>
                {swap.after}
              </>
            ) : (
              segment.text
            )}
          </Text>

          {segment.sourceLabel && !segment.sourceUrl ? (
            <Text size="xs" className="text-typography-700">
              {REPLY_DRAFT_COPY.source(segment.sourceLabel)}
            </Text>
          ) : null}
        </VStack>

        {swap ? (
          <Pressable
            onPress={() => onSwap(segment)}
            className="h-12 w-12 items-center justify-center rounded-md web:cursor-pointer data-[hover=true]:bg-background-50"
            testID={`reply-draft-swap-${segment.id}`}
            {...getAccessibilityProps({
              role: 'button',
              label: REPLY_DRAFT_COPY.swapTo(swap.next),
            })}>
            <Icon as={ArrowLeftRight} size="md" className="text-typography-700" />
          </Pressable>
        ) : null}

        {isOut ? (
          <PointButton
            kind="add"
            label={REPLY_DRAFT_COPY.addLabel(segment.text)}
            onPress={() => onAdd(segment)}
            testID={`reply-draft-add-${segment.id}`}
          />
        ) : (
          <PointButton
            kind="remove"
            label={REPLY_DRAFT_COPY.removeLabel(segment.text)}
            onPress={() => onRemove(segment)}
            testID={`reply-draft-remove-${segment.id}`}
          />
        )}
      </HStack>

      <PointSource segment={segment} />
    </VStack>
  );
});

/**
 * Where a point came from, as a link of its own under the sentence, apart from
 * the minus and plus so opening a source never changes the reply.
 */
function PointSource({ segment }: { segment: Segment }) {
  if (!segment.sourceUrl) {
    return null;
  }

  const label = segment.sourceLabel ?? segment.sourceUrl;

  return (
    <SourceLink
      url={segment.sourceUrl}
      publicationId={segment.publicationId}
      title={label}
      from="sentence"
      text={REPLY_DRAFT_COPY.source(label)}
      accessibilityLabel={REPLY_DRAFT_COPY.openSource(label)}
      testID={`reply-draft-source-${segment.id}`}
      className="ml-2"
    />
  );
}
